import { buildApiUrl } from "../utils/apiConfig";
import { collegeNamesMatch } from "../utils/extractListedColleges";
import { apiClient } from "../utils/apiClient";

type Listener = (names: string[]) => void;

interface AthleteCollegeRow {
    college?: { name?: string };
    name?: string;
}

let cachedUsername = "";
let cachedNames: string[] = [];
let loadedUsername = "";
let inflight: Promise<string[]> | null = null;
const listeners = new Set<Listener>();

function notify(): void {
    for (const listener of listeners) {
        listener(cachedNames);
    }
}

function namesFromRows(rows: unknown): string[] {
    if (!Array.isArray(rows)) {
        return [];
    }
    return (rows as AthleteCollegeRow[])
        .map((row) => row.college?.name ?? row.name ?? "")
        .map((name) => name.trim())
        .filter(Boolean);
}

export function peekAthleteCollegeNames(username: string): string[] {
    return cachedUsername === username ? cachedNames : [];
}

export function hasAthleteCollegeListLoaded(username: string): boolean {
    return loadedUsername === username;
}

export function subscribeAthleteCollegeNames(listener: Listener): () => void {
    listeners.add(listener);
    listener(cachedNames);
    return () => {
        listeners.delete(listener);
    };
}

export async function loadAthleteCollegeNames(username: string): Promise<string[]> {
    if (!username) {
        return [];
    }
    if (inflight && cachedUsername === username) {
        return inflight;
    }
    cachedUsername = username;
    inflight = (async () => {
        try {
            const res = await apiClient(buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(username)}`), {
                credentials: "include",
                headers: { Accept: "application/json" },
            });
            const names = res.ok ? namesFromRows(await res.json()) : [];
            if (cachedUsername === username) {
                cachedNames = names;
                loadedUsername = username;
                notify();
            }
            return names;
        } catch {
            if (cachedUsername === username) {
                loadedUsername = username;
                notify();
            }
            return cachedUsername === username ? cachedNames : [];
        } finally {
            inflight = null;
        }
    })();
    return inflight;
}

export function rememberAthleteCollegeName(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) {
        return;
    }
    if (cachedNames.some((existing) => collegeNamesMatch(existing, trimmed))) {
        return;
    }
    cachedNames = [...cachedNames, trimmed];
    notify();
}

export function isAthleteCollegeOnList(name: string, names: string[] = cachedNames): boolean {
    return names.some((existing) => collegeNamesMatch(existing, name));
}

export function clearAthleteCollegeNameCache(): void {
    cachedUsername = "";
    cachedNames = [];
    loadedUsername = "";
    inflight = null;
    notify();
}
